import * as yup from 'yup';
import {
  MEDICAL_CONDITIONS,
  PARQ_QUESTIONS,
  getRequiredDocumentKeys,
  hasRequiredStudentDocumentAck,
  isFamilyPackage,
  isStudentPackage,
} from './hpcFormConfig';

const yesNoRequired = () =>
  yup.string().oneOf(['yes', 'no'], 'Please select Yes or No').required('Please select Yes or No');

const medicalShape = MEDICAL_CONDITIONS.reduce(
  (acc, { key }) => ({ ...acc, [key]: yesNoRequired() }),
  {},
);

const parqShape = PARQ_QUESTIONS.reduce(
  (acc, { key }) => ({ ...acc, [key]: yesNoRequired() }),
  {},
);

const documentsAckSchema = yup.object().test('documentsAck', function validateDocuments(value) {
  const membershipType = this.parent.membershipType;
  const parqAnswers = this.parent.parqAnswers;
  const requiredKeys = getRequiredDocumentKeys(membershipType, parqAnswers, this.parent.age);

  if (!value || typeof value !== 'object') {
    return this.createError({ message: 'Please confirm the required documents.' });
  }

  for (const key of requiredKeys) {
    if (value[key] !== true) {
      return this.createError({
        path: `documentsAck.${key}`,
        message: `Please confirm you will provide this document.`,
      });
    }
  }

  if (isStudentPackage(membershipType) && !hasRequiredStudentDocumentAck(value)) {
    return this.createError({
      path: 'documentsAck.student_id',
      message: 'Please confirm you will provide a Student ID or enrollment letter.',
    });
  }

  return true;
});

export const stepSchemas = [
  yup.object({
    membershipType: yup.string().required('Please select a membership package'),
  }),
  yup.object({
    fullName: yup.string().trim().required('Full name is required'),
    dateOfBirth: yup.string().required('Date of birth is required'),
    age: yup
      .number()
      .typeError('Please enter a valid age')
      .min(1, 'Please enter a valid age')
      .max(120, 'Please enter a valid age')
      .required('Age is required'),
    gender: yup.string().oneOf(['male', 'female', 'other']).required('Gender is required'),
    nicPassport: yup.string().trim(),
    nationality: yup.string().trim().required('Nationality is required'),
    occupation: yup.string().trim().required('Occupation is required'),
    address: yup.string().trim().required('Address is required'),
  }),
  yup.object({
    mobileNumber: yup.string().trim().required('Mobile number is required'),
    whatsappNumber: yup.string().trim().required('WhatsApp number is required'),
    email: yup.string().trim().email('Please enter a valid email address').required('Email address is required'),
    emergencyContactPerson: yup.string().trim().required('Emergency contact name is required'),
    emergencyContactRelationship: yup.string().trim().required('Relationship is required'),
    emergencyContactNumber: yup.string().trim().required('Emergency contact number is required'),
  }),
  yup.object({
    trainingGoals: yup.array().min(1, 'Please select at least one training goal'),
    preferredTrainingTimes: yup.array().min(1, 'Please select at least one preferred training time'),
    membershipStartDate: yup.string().required('Membership start date is required'),
    trainerPreference: yup.string(),
  }),
  yup.object({
    medicalConditions: yup.object().shape(medicalShape),
    takingMedication: yesNoRequired(),
    medicationDetails: yup.string().when('takingMedication', {
      is: 'yes',
      then: (s) => s.trim().required('Please specify medication'),
      otherwise: (s) => s,
    }),
    hasInjuries: yesNoRequired(),
    injuryDetails: yup.string().when('hasInjuries', {
      is: 'yes',
      then: (s) => s.trim().required('Please explain injuries or limitations'),
      otherwise: (s) => s,
    }),
    doctorRecommendations: yup.string(),
  }),
  yup.object({
    parqAnswers: yup.object().shape(parqShape),
  }),
  yup.object({
    liabilityAgreed: yup.boolean().oneOf([true], 'You must accept the terms and conditions'),
  }),
  yup.object({
    paymentMethod: yup.string().required('Please select a payment method'),
    membershipFee: yup
      .number()
      .typeError('Please select a membership package on the Membership step')
      .min(0, 'Please select a membership package on the Membership step')
      .required('Please select a membership package on the Membership step'),
    registrationFee: yup
      .number()
      .typeError('Please select a membership package on the Membership step')
      .min(0, 'Please select a membership package on the Membership step')
      .required('Please select a membership package on the Membership step'),
    totalAmountPaid: yup
      .number()
      .typeError('Please select a membership package on the Membership step')
      .min(0, 'Please select a membership package on the Membership step')
      .required('Please select a membership package on the Membership step'),
    paymentDate: yup.string().required('Payment date is required'),
    receiptNumber: yup.string(),
  }),
  yup.object({
    mediaConsent: yup.string().oneOf(['allow', 'deny']).required('Please select a media consent option'),
    declarationName: yup.string().trim().required('Member name is required'),
    declarationSignature: yup.string().trim().required('Please type your full name as your signature'),
    declarationDate: yup.string().required('Declaration date is required'),
    membershipType: yup.string(),
    parqAnswers: yup.object(),
    documentsAck: documentsAckSchema,
  }),
  yup.object({}),
];

const submemberDocumentsAckSchema = yup.object().test(
  'submemberDocumentsAck',
  function validateSubmemberDocuments(value) {
    const membershipType = this.parent.membershipType;
    const parqAnswers = this.parent.parqAnswers;
    const requiredKeys = getRequiredDocumentKeys(membershipType, parqAnswers, this.parent.age);
    requiredKeys.delete('payment_proof');

    if (!value || typeof value !== 'object') {
      return this.createError({ message: 'Please confirm the required documents.' });
    }

    const missingKey = [...requiredKeys].find((key) => value[key] !== true);
    if (missingKey) {
      return this.createError({
        path: `documentsAck.${missingKey}`,
        message: 'Please confirm you will provide this document.',
      });
    }

    if (isStudentPackage(membershipType) && !hasRequiredStudentDocumentAck(value)) {
      return this.createError({
        path: 'documentsAck.student_id',
        message: 'Please confirm you will provide a Student ID or enrollment letter.',
      });
    }
    return true;
  },
);

const submemberFinishSchema = yup.object({
  mediaConsent: yup.string().oneOf(['allow', 'deny']).required('Please select media consent'),
  declarationName: yup.string().trim().required('Member name is required'),
  declarationSignature: yup.string().trim().required('Member signature is required'),
  declarationDate: yup.string().required('Declaration date is required'),
  documentsAck: submemberDocumentsAckSchema,
});

const SUBMEMBER_STEP_SCHEMA_INDEX = {
  personal: 1,
  contact: 2,
  membership: 3,
  health: 4,
  parq: 5,
  agreement: 6,
};

export const validateSubmemberStep = async (values, membershipType, stepKey) => {
  const errors = {};
  const schema =
    stepKey === 'finish'
      ? submemberFinishSchema
      : stepSchemas[SUBMEMBER_STEP_SCHEMA_INDEX[stepKey]];
  try {
    await schema.validate({ ...values, membershipType }, { abortEarly: false });
  } catch (error) {
    (error.inner || []).forEach((item) => {
      if (item.path) errors[item.path] = item.message;
    });
  }
  if (stepKey === 'personal' && Number(values.age) < 13) {
    errors.age = 'Couple membership is available to members aged 13 and above.';
  }
  return errors;
};

export const validateSubmemberValues = async (values, membershipType) => {
  const errors = {};
  const schemaIndexes = [1, 2, 3, 4, 5, 6];
  for (const index of schemaIndexes) {
    try {
      await stepSchemas[index].validate(values, { abortEarly: false });
    } catch (error) {
      (error.inner || []).forEach((item) => {
        if (item.path) errors[item.path] = item.message;
      });
    }
  }

  try {
    await submemberFinishSchema.validate(
      { ...values, membershipType },
      { abortEarly: false },
    );
  } catch (error) {
    (error.inner || []).forEach((item) => {
      if (item.path) errors[item.path] = item.message;
    });
  }
  if (Number(values.age) < 13) {
    errors.age = 'Couple membership is available to members aged 13 and above.';
  }
  return errors;
};

// Family members have no minimum age; the server works out each age from the date of birth.
const dropFamilyAgeErrors = (errors, membershipType) => {
  if (isFamilyPackage(membershipType) && errors.age) {
    const next = { ...errors };
    delete next.age;
    return next;
  }
  return errors;
};

const familyMemberStepErrors = async (values, membershipType, stepKey) => {
  const errors = {};
  const schema =
    stepKey === 'finish'
      ? submemberFinishSchema
      : stepSchemas[SUBMEMBER_STEP_SCHEMA_INDEX[stepKey]];
  try {
    await schema.validate({ ...values, membershipType }, { abortEarly: false });
  } catch (error) {
    (error.inner || []).forEach((item) => {
      if (item.path) errors[item.path] = item.message;
    });
  }
  return dropFamilyAgeErrors(errors, membershipType);
};

export const validateFamilyMemberStep = (values, membershipType, stepKey) =>
  familyMemberStepErrors(values, membershipType, stepKey);

export const validateFamilyMemberValues = async (values, membershipType) => {
  let errors = {};
  for (const stepKey of [...Object.keys(SUBMEMBER_STEP_SCHEMA_INDEX), 'finish']) {
    errors = { ...errors, ...(await familyMemberStepErrors(values, membershipType, stepKey)) };
  }
  return errors;
};
